// Detects whether an imported file is WAV or MP3 by sniffing magic bytes
// (not just the filename extension, so a mis-extensioned file is still
// classified correctly). MP3 has no reliable way to determine from the file
// alone whether the encoder used true independent stereo or joint/mid-side
// stereo (which mixes channel information together for smaller file size).
// Because of that, this module cannot make the same channel-safety guarantee
// for MP3 that wavInspect.ts makes for WAV — every MP3 import must be treated
// as "channel separation not verified" regardless of what its header says.

import type { AudioFileFormat } from './types';

export async function detectAudioFormat(file: Blob): Promise<AudioFileFormat | null> {
  let header: Uint8Array;
  try {
    header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  } catch {
    // A stored Blob that can't be sliced/read is treated as unrecognized
    // rather than crashing the caller; the caller surfaces this as
    // "unsupported"/"unreadable" through its own status handling.
    return null;
  }

  if (header.length >= 12) {
    const riff = String.fromCharCode(header[0], header[1], header[2], header[3]);
    const wave = String.fromCharCode(header[8], header[9], header[10], header[11]);
    if (riff === 'RIFF' && wave === 'WAVE') return 'wav';
  }

  if (header.length >= 3 && header[0] === 0x49 && header[1] === 0x44 && header[2] === 0x33) {
    return 'mp3'; // "ID3" tag prefix, present on most MP3 files
  }

  if (header.length >= 2 && header[0] === 0xff && (header[1] & 0xe0) === 0xe0) {
    return 'mp3'; // bare MPEG frame sync with no ID3 tag
  }

  return null;
}

/**
 * Reads a file's duration via a temporary HTMLAudioElement, for display
 * purposes only (Song Library metadata). This does not decode PCM, alter
 * playback, or feed the actual performance audio path — it exists because
 * MP3 has no fixed-size header to compute duration from directly the way
 * wavInspect.ts does for WAV. Resolves to null (never rejects/hangs) if the
 * browser environment can't determine a duration, so a probe failure can
 * never block importing the file.
 */
export function probeDurationSeconds(file: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    if (typeof Audio === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
      resolve(null);
      return;
    }
    let settled = false;
    const settle = (value: number | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      element.removeAttribute('src');
      resolve(value);
    };

    let url: string;
    let element: HTMLAudioElement;
    try {
      url = URL.createObjectURL(file);
      element = new Audio();
    } catch {
      resolve(null);
      return;
    }

    element.addEventListener('loadedmetadata', () => settle(Number.isFinite(element.duration) ? element.duration : null), {
      once: true,
    });
    element.addEventListener('error', () => settle(null), { once: true });
    // Safety timeout: never let a probe hang forever if neither event fires
    // (e.g. jsdom in unit tests, which implements Audio/createObjectURL but
    // never actually decodes media or emits loadedmetadata/error).
    setTimeout(() => settle(null), 300);

    element.preload = 'metadata';
    element.src = url;
  });
}
